import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useProducts } from '../../hooks/useFirestore'
import { useCategories } from '../../hooks/useCategories'
import { useCart } from '../../hooks/useCart'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { getImageUrl } from '../../services/cloudinary.service'
import Toast from '../../components/Toast'
import { formatCurrency } from '../../utils/helpers'

const buildEmbedUrl = (url) => {
  const raw = String(url || '').trim()
  if (!raw) return null
  if (raw.includes('youtube.com/embed/')) return raw
  const ytMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]+)/)
  if (ytMatch?.[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`
  const igMatch = raw.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  if (igMatch?.[1] && igMatch?.[2]) return `https://www.instagram.com/${igMatch[1]}/${igMatch[2]}/embed`
  const ttMatch = raw.match(/tiktok\.com\/.+\/video\/(\d+)/)
  if (ttMatch?.[1]) return `https://www.tiktok.com/embed/v2/${ttMatch[1]}`
  if (raw.includes('facebook.com')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(raw)}&show_text=false&width=500`
  }
  return null
}

const resolveImageSource = (image) => {
  if (!image) return null
  if (typeof image === 'string') return image
  if (typeof image === 'object') return image.fileId || image.url || image.thumbnailUrl || null
  return null
}

const getVariantMedia = (product, transformations) => {
  const media = []
  const seen = new Set()
  const variants = Array.isArray(product?.variants) ? product.variants : []
  variants.forEach(variant => {
    const variantImages = Array.isArray(variant?.images) ? variant.images : []
    variantImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
    const variantVideos = Array.isArray(variant?.videos) ? variant.videos : []
    variantVideos.forEach(video => {
      const embedUrl = buildEmbedUrl(video)
      if (!embedUrl) return
      const key = `video:${embedUrl}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'video', src: embedUrl })
      }
    })
  })
  if (media.length === 0) {
    const fallbackImages = Array.isArray(product?.images) ? product.images : []
    fallbackImages.forEach(image => {
      const source = resolveImageSource(image)
      if (!source) return
      const url = getImageUrl(source, transformations)
      const key = `image:${url}`
      if (!seen.has(key)) {
        seen.add(key)
        media.push({ type: 'image', src: url })
      }
    })
  }
  return media
}

function VariantMediaCarousel({ media, alt }) {
  const safeMedia = useMemo(() => (media.length > 0 ? media : [{ type: 'image', src: getImageUrl(null) }]), [media])
  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [media])

  useEffect(() => {
    if (safeMedia.length <= 1) return
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % safeMedia.length)
    }, 3500)
    return () => clearInterval(id)
  }, [safeMedia])

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div
        className="flex h-full w-full transition-transform duration-500"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {safeMedia.map((item, idx) => (
          <div key={`${item.type}-${item.src}-${idx}`} className="h-full w-full flex-shrink-0">
            {item.type === 'video' ? (
              <iframe
                src={item.src}
                title={`Video ${idx + 1}`}
                className="h-full w-full pointer-events-none"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            ) : (
              <img alt={alt} className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105" src={item.src} loading="lazy" />
            )}
          </div>
        ))}
      </div>
      {safeMedia.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {safeMedia.map((_, idx) => (
            <button
              key={idx}
              type="button"
              className={`h-2 w-2 rounded-full border border-white/80 ${idx === index ? 'bg-white' : 'bg-white/50'}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setIndex(idx)
              }}
              aria-label={`Imagen ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const SORTS = {
  destacados: { label: 'Destacados', orderByField: 'featured', orderDirection: 'desc' },
  precio_asc: { label: 'Precio: Menor a Mayor', orderByField: 'price', orderDirection: 'asc' },
  precio_desc: { label: 'Precio: Mayor a Menor', orderByField: 'price', orderDirection: 'desc' },
  recientes: { label: 'Más Recientes', orderByField: 'createdAt', orderDirection: 'desc' }
}

function FashionProducts({ offersOnly = false }) {
  const navigate = useNavigate()
  const { addToCart } = useCart()
  const { categories } = useCategories(false)
  const { rules } = useBusinessRulesContext()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }
  const [notification, setNotification] = useState(null)
  const [searchParams] = useSearchParams()
  const categoryId = offersOnly ? null : (searchParams.get('categoria') || null)

  const [sortKey, setSortKey] = useState('destacados')
  const [search, setSearch] = useState('')
  const [maxPrice, setMaxPrice] = useState(null)

  const sort = SORTS[sortKey] || SORTS.destacados

  const { products, loading } = useProducts(
    {
      categoryId,
      orderByField: sort.orderByField,
      orderDirection: sort.orderDirection,
      maxPrice: maxPrice !== null ? Number(maxPrice) : null
    },
    [categoryId, sortKey, maxPrice]
  )

  const activeProducts = useMemo(() => {
    const base = (products || []).filter(p => p?.active !== false)
    const baseFiltered = offersOnly
      ? base.filter(p => p.specialPrice > 0 && p.specialPrice < p.price)
      : base
    const stockFiltered = outOfStockPolicy.visibility === 'hide'
      ? baseFiltered.filter(p => (p?.stock || 0) > 0)
      : baseFiltered
    const term = search.trim().toLowerCase()
    if (!term) return stockFiltered
    return stockFiltered.filter(p => {
      const hay = `${p.name || ''} ${p.brand || ''}`.toLowerCase()
      return hay.includes(term)
    })
  }, [products, search, offersOnly, outOfStockPolicy.visibility])

  const categoryName = useMemo(() => {
    if (!categoryId) return null
    const found = (categories || []).find(c => c.id === categoryId)
    return found?.name || null
  }, [categories, categoryId])

  const maxPriceAvailable = useMemo(() => {
    const prices = activeProducts.map(p => {
      const hasDiscount = p.specialPrice > 0 && p.specialPrice < p.price
      return hasDiscount ? p.specialPrice : p.price
    }).filter(n => typeof n === 'number' && !Number.isNaN(n))
    if (prices.length === 0) return 0
    return Math.max(...prices)
  }, [activeProducts])

  return (
    <main className="w-full flex flex-col min-h-screen px-4 md:px-10 lg:px-20 xl:px-40 pb-20">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        duration={notification?.duration ?? 3000}
        actionLabel={notification?.action?.label}
        onAction={notification?.action?.onClick}
        onClose={() => setNotification(null)}
      />
      <div className="flex flex-wrap gap-2 py-6 text-sm">
        <Link className="text-gray-500 font-medium hover:text-[#c27e70] transition-colors" to="/">
          Inicio
        </Link>
        <span className="text-gray-400">/</span>
        {offersOnly ? (
          <span className="text-[#181211] font-bold">Ofertas</span>
        ) : (
          <>
            <Link className="text-gray-500 font-medium hover:text-[#c27e70] transition-colors" to="/productos">
              Catálogo
            </Link>
            {categoryName ? (
              <>
                <span className="text-gray-400">/</span>
                <span className="text-[#181211] font-bold">{categoryName}</span>
              </>
            ) : null}
          </>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-12">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <div className="flex items-center justify-between pb-4 border-b border-[#e1d7d6] mb-2">
              <h2 className="text-xl font-bold tracking-tight">Filtros</h2>
              <button
                type="button"
                className="text-xs font-bold text-gray-500 hover:text-[#c27e70] uppercase tracking-wide"
                onClick={() => setMaxPrice(null)}
              >
                REINICIAR
              </button>
            </div>

            <details className="group py-4 border-b border-[#e1d7d6]" open>
              <summary className="flex cursor-pointer items-center justify-between font-medium list-none">
                <span className="text-sm font-bold">Precio</span>
                <span className="material-symbols-outlined text-gray-500 group-open:rotate-180 transition-transform duration-300">expand_more</span>
              </summary>
              <div className="pt-4 px-1">
                <input
                  className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#c27e70]"
                  min="0"
                  max={maxPriceAvailable || 0}
                  type="range"
                  value={maxPrice !== null ? maxPrice : (maxPriceAvailable || 0)}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
                <div className="flex justify-between mt-2 text-sm text-gray-500">
                  <span>$0</span>
                  <span className="font-medium text-[#181211]">
                    {formatCurrency(maxPrice !== null ? Number(maxPrice) : Number(maxPriceAvailable || 0))}
                  </span>
                </div>
              </div>
            </details>

            {!offersOnly ? (
              <details className="group py-4 border-b border-[#e1d7d6]" open>
                <summary className="flex cursor-pointer items-center justify-between font-medium list-none">
                  <span className="text-sm font-bold">Categoría</span>
                  <span className="material-symbols-outlined text-gray-500 group-open:rotate-180 transition-transform duration-300">expand_more</span>
                </summary>
                <div className="pt-4 flex flex-col gap-2">
                  <Link className={`text-sm font-medium ${!categoryId ? 'text-[#c27e70]' : 'text-gray-600'} hover:text-[#c27e70]`} to="/productos">
                    Todas
                  </Link>
                  {(categories || []).filter(c => c?.active !== false).map((c) => (
                    <Link
                      key={c.id}
                      className={`text-sm font-medium ${categoryId === c.id ? 'text-[#c27e70]' : 'text-gray-600'} hover:text-[#c27e70]`}
                      to={`/productos?categoria=${encodeURIComponent(c.id)}`}
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </details>
            ) : null}
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <p className="text-sm text-gray-500">
              <span className="font-bold text-[#181211]">{activeProducts.length}</span> Productos Encontrados
            </p>

            <div className="flex gap-4 items-center w-full sm:w-auto">
              <div className="flex items-center gap-2 bg-white border border-[#eee] rounded-xl px-4 h-10 shadow-sm w-full sm:w-[260px]">
                <span className="material-symbols-outlined text-[20px] text-gray-500">search</span>
                <input
                  className="w-full outline-none text-sm"
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="relative">
                <select
                  className="h-10 rounded-xl border border-[#eee] bg-white px-3 text-sm font-medium shadow-sm outline-none"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  {Object.keys(SORTS).map((k) => (
                    <option key={k} value={k}>{SORTS[k].label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">Cargando productos...</div>
          ) : activeProducts.length === 0 ? (
            <div className="text-sm text-gray-500">
              No se encontraron productos para los filtros seleccionados.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {activeProducts.map((p) => {
                const media = getVariantMedia(p, { width: 800, height: 1000, crop: 'fill' })
                const hasDiscount = p.specialPrice > 0 && p.specialPrice < p.price
                const price = hasDiscount ? p.specialPrice : p.price

                return (
                  <div
                    key={p.id}
                    className="group flex flex-col gap-3 cursor-pointer"
                    onClick={() => navigate(`/productos/${p.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/productos/${p.id}`)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg bg-gray-100">
                      {hasDiscount ? (
                        <div className="absolute left-3 top-3 z-10">
                          <span className="bg-[#c27e70] text-white text-xs font-bold px-2 py-1 rounded shadow-sm uppercase tracking-wider">-</span>
                        </div>
                      ) : null}
                      <VariantMediaCarousel media={media} alt={p.name} />
                      <div className="absolute inset-x-0 bottom-4 flex justify-center opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 z-20 px-4">
                        <Link
                          to={`/productos/${p.id}`}
                          className="w-full bg-white text-[#181211] font-bold text-sm py-3 rounded-lg shadow-lg hover:bg-[#c27e70] hover:text-white transition-colors flex items-center justify-center gap-2"
                        >
                          <span className="material-symbols-outlined text-[18px]">visibility</span>
                          Vista Rápida
                        </Link>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-start">
                        <Link to={`/productos/${p.id}`} className="text-base font-bold text-[#181211] group-hover:text-[#c27e70] transition-colors">
                          {p.name}
                        </Link>
                      </div>
                      <div className="flex gap-2 items-center">
                        <p className={`text-sm font-medium ${hasDiscount ? 'text-[#c27e70] font-bold' : 'text-gray-600'}`}>
                          {formatCurrency(price)}
                        </p>
                        {hasDiscount ? (
                          <p className="text-sm font-normal text-gray-400 line-through">{formatCurrency(p.price)}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        className="mt-2 w-full border border-[#e8e6e1] bg-white hover:bg-[#c27e70] hover:text-white transition-colors rounded-lg py-2 text-sm font-bold"
                        onClick={(e) => {
                          e.stopPropagation()
                          const stock = p?.stock || 0
                          if (stock <= 0 && outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
                            if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
                              setNotification({ type: 'error', message: 'Este producto no tiene stock. Disponible solo para consulta.', duration: 5000 })
                              navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
                              return
                            }
                            setNotification({ type: 'error', message: 'Este producto no tiene stock disponible.', duration: 5000 })
                            return
                          }
                          addToCart(p, null, 1)
                          setNotification({
                            type: 'success',
                            message: `Agregado al carrito: ${p.name}`,
                            duration: 6000,
                            action: { label: 'Ver carrito', onClick: () => navigate('/carrito') }
                          })
                        }}
                      >
                        Añadir Rápido
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default FashionProducts
